import { FC, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchData, getTranslatedValue } from '../../../../reusable'
import { PageTitle } from '../../../../_metronic/layout/core'
import { KTIcon } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import Loader from '../../component/Loader'
import moment from 'moment'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import { getStatusBadgeClass, advertisementStatuses } from './AddAdvertisement.constants'
import { CATEGORY_CONFIG } from './AddAdvertisementPromotionSelect'

const AdvertisementDetail: FC = () => {
  const t = Tr()
  const navigate = useNavigate()
  const location = useLocation()
  const lang = useLang()

  const locationState = location.state as any

  var vendorId = ''
  const vendorData = sessionStorage.getItem("vendor")
  if (vendorData) {
    const vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }

  const [isLoading, setIsLoading] = useState(false)
  const [adData, setAdData] = useState<any>(null)
  const [imageBaseUrl, setImageBaseUrl] = useState('')
  const [mediaImages, setMediaImages] = useState<any[]>([])
  const [lightboxImg, setLightboxImg] = useState<string | null>(null)

  // Promotion items resolved from actionType / actionTypeId
  const [promotionParentName, setPromotionParentName] = useState<string>('')
  const [promotionChildren, setPromotionChildren] = useState<{ id: string; name: string }[]>([])
  const [promotionLoading, setPromotionLoading] = useState(false)

  const adsId = locationState?.adsId ? String(locationState.adsId) : ''

  useEffect(() => {
    document.title = 'Insayr | Advertisement Detail'
    if (!adsId) {
      navigate('/viewbanneradvertisment')
      return
    }
    fetchAdDetail(adsId)
    fetchMediaImages(adsId)
  }, [])

  const fetchAdDetail = async (id: string) => {
    setIsLoading(true)
    try {
      const res: any = await fetchData('public/getad', {
        vendorId: Number(vendorId),
        adsId: Number(id),
      })
      if (String(res?.status) === '200') {
        setAdData(res.data)
        setImageBaseUrl(res.url || '')
      }
    } catch (err) {
      console.error('Failed to fetch ad detail', err)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchMediaImages = async (id: string) => {
    try {
      const res: any = await fetchData('public/getadsmedia', {
        vendorId: String(vendorId),
        adsId: Number(id),
      })
      if (String(res?.status) === '200') {
        setMediaImages(Array.isArray(res.data) ? res.data : [])
      }
    } catch (err) {
      console.error('Failed to fetch media images', err)
    }
  }

  // Resolve promotion parent + child names when adData arrives
  useEffect(() => {
    if (!adData) return
    const categoryId = sessionStorage.getItem('categoryId') || ''
    const cfg = CATEGORY_CONFIG[categoryId]
    if (!cfg) return

    const childIds: string[] = Array.isArray(adData.actionTypeId)
      ? adData.actionTypeId.map(String)
      : []
    if (!childIds.length) return

    if (cfg.parentOnly) {
      const resolveRestaurant = async () => {
        try {
          const parentRes: any = await fetchData(cfg.parentApi, cfg.parentParam(vendorId))
          if (String(parentRes?.status) !== '200') return
          const restaurant = (parentRes.data || []).find((item: any) =>
            childIds.includes(String(item[cfg.parentIdKey]))
          )
          if (restaurant) {
            const getName = (item: any): string => {
              if (item.en_name) return item.en_name
              const key = Object.keys(item).find(
                (k) => k.startsWith('en_') && typeof item[k] === 'string' && item[k]
              )
              return key ? item[key] : `#${item[cfg.parentIdKey]}`
            }
            setPromotionParentName(getName(restaurant))
            setPromotionChildren([])
          }
        } catch (err) {
          console.error('Failed to resolve restaurant', err)
        } finally {
          setPromotionLoading(false)
        }
      }

      setPromotionLoading(true)
      resolveRestaurant()
      return
    }

    setPromotionLoading(true)

    const resolve = async () => {
      try {
        // Load all parents for this vendor
        const parentRes: any = await fetchData(cfg.parentApi, cfg.parentParam(vendorId))
        if (String(parentRes?.status) !== '200') return
        const parents: any[] = parentRes.data || []

        for (const parent of parents) {
          const parentId = String(parent[cfg.parentIdKey])
          const childRes: any = await fetchData(cfg.childApi, cfg.childParam(parentId))
          if (String(childRes?.status) !== '200') continue

          const children: any[] = childRes.data?.[0]?.[cfg.childListKey] || []
          const matched = children.filter((c: any) =>
            childIds.includes(String(c[cfg.childIdKey]))
          )
          if (matched.length > 0) {
            const getName = (item: any): string => {
              if (item.en_name) return item.en_name
              const key = Object.keys(item).find(
                (k) => k.startsWith('en_') && typeof item[k] === 'string' && item[k]
              )
              return key ? item[key] : `#${item[cfg.childIdKey]}`
            }
            const getParentName = (item: any): string => {
              if (item.en_name) return item.en_name
              const key = Object.keys(item).find(
                (k) => k.startsWith('en_') && typeof item[k] === 'string' && item[k]
              )
              return key ? item[key] : `#${parentId}`
            }
            setPromotionParentName(getParentName(parent))
            setPromotionChildren(
              matched.map((c: any) => ({ id: String(c[cfg.childIdKey]), name: getName(c) }))
            )
            return
          }
        }
      } catch (err) {
        console.error('Failed to resolve promotion items', err)
      } finally {
        setPromotionLoading(false)
      }
    }

    resolve()
  }, [adData])

  const getStatusDisplay = (status: string) => {
    return advertisementStatuses.find((item) => item.api === status)?.display || status || '-'
  }

  const getTargetValue = (targets: any[], masterId: number) => {
    if (!Array.isArray(targets)) return '-'
    const target = targets.find((t: any) => t.adstargetmasterId === masterId)
    if (!target || !target.isActive || !target.value?.length) return t('All')

    if (masterId === 1) {
      const [from, to] = target.value
      if (from === 0 && to === 0) return t('All')
      if (from === 45 && to === 100) return '45+'
      return `${from} - ${to}`
    }
    if (masterId === 4) {
      return target.value[0] === 0 ? t('All') : String(target.value[0])
    }
    return target.value.join(', ')
  }

  if (isLoading || !adData) {
    return (
      <>
        {isLoading && <Loader />}
        <PageTitle breadcrumbs={[]}>{t('Advertisement Detail')}</PageTitle>
        {!isLoading && !adData && (
          <div className='card card-flush py-4'>
            <div className='card-body text-center py-10'>
              <p className='text-muted fs-5'>{t('No data found.')}</p>
              <button className='btn btn-primary' onClick={() => navigate('/viewbanneradvertisment')}>
                {t('Back to List')}
              </button>
            </div>
          </div>
        )}
      </>
    )
  }

  const bannerImageUrl = adData?.banner?.bannerImage || adData?.banner?.image_312x152
    ? `${imageBaseUrl}${adData?.banner?.bannerImage || adData?.banner?.image_312x152}`
    : null

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('Advertisement Detail')}</PageTitle>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className='position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
          style={{ background: 'rgba(0,0,0,0.75)', zIndex: 9999 }}
          onClick={() => setLightboxImg(null)}
        >
          <img
            src={lightboxImg}
            alt='preview'
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px' }}
          />
          <button
            className='btn btn-icon btn-light position-absolute top-0 end-0 m-4'
            onClick={() => setLightboxImg(null)}
          >
            <KTIcon iconName='cross' className='fs-1' />
          </button>
        </div>
      )}

      <div className='d-flex flex-column gap-6'>

        {/* Header card */}
        <div className='card card-flush'>
          <div className='card-body py-5'>
            <div className='d-flex align-items-center justify-content-between flex-wrap gap-3'>
              <div className='d-flex align-items-center gap-4'>
                <button
                  className='btn btn-sm btn-icon btn-light me-1'
                  onClick={() => navigate('/viewbanneradvertisment')}
                  title={t('Back')}
                >
                  <KTIcon iconName='arrow-left' className='fs-2' />
                </button>
                <div>
                  <h2 className='mb-1 fw-bold'>
                    {adData ? (getTranslatedValue(lang, adData, '_name') || adData.en_name || '-') : '-'}
                  </h2>
                  <span className='text-muted fs-6'>{adData?.placementName ||(locationState as any)?.placementName || '-'}</span>
                </div>
              </div>
              <span
                className={`btn btn-sm fw-bold pointer-events-none ${getStatusBadgeClass(adData?.status)}`}
              >
                {t(getStatusDisplay(adData?.status))}
              </span>
            </div>
          </div>
        </div>

        <div className='row g-6'>

          {/* Left column */}
          <div className='col-lg-8 d-flex flex-column gap-6'>

            {/* Banner Image */}
            {bannerImageUrl && (
              <div className='card card-flush'>
                <div className='card-header pt-5'>
                  <h4 className='card-title fw-bold'>{t('Banner Image')}</h4>
                </div>
                <div className='card-body pt-2'>
                  <img
                    src={bannerImageUrl}
                    alt='banner'
                    className='rounded w-100 cursor-pointer'
                    style={{ maxHeight: '300px', objectFit: 'contain', background: '#f5f5f5' }}
                    onClick={() => setLightboxImg(bannerImageUrl)}
                  />
                </div>
              </div>
            )}

            {/* Banner Content */}
            {adData?.banner && (
              <div className='card card-flush'>
                <div className='card-header pt-5'>
                  <h4 className='card-title fw-bold'>{t('Banner Content')}</h4>
                </div>
                <div className='card-body pt-2'>
                  <div className='row g-4'>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Title (English)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.en_title || '-'}</p>
                    </div>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Title (Russian)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.ru_title || '-'}</p>
                    </div>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Title (Uzbek)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.uz_title || '-'}</p>
                    </div>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Subtitle (English)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.en_subtitle || '-'}</p>
                    </div>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Subtitle (Russian)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.ru_subtitle || '-'}</p>
                    </div>
                    <div className='col-md-4'>
                      <label className='text-muted fw-semibold fs-7 mb-1'>{t('Subtitle (Uzbek)')}</label>
                      <p className='fw-bold fs-6 mb-0'>{adData.banner.uz_subtitle || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Ad Details */}
            <div className='card card-flush'>
              <div className='card-header pt-5'>
                <h4 className='card-title fw-bold'>{t('Ad Details')}</h4>
              </div>
              <div className='card-body pt-2'>
                <div className='row g-4'>
                  <div className='col-md-6'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Name (English)')}</label>
                    <p className='fw-bold fs-6 mb-0'>{adData?.en_name || '-'}</p>
                  </div>
                  <div className='col-md-6'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Name (Russian)')}</label>
                    <p className='fw-bold fs-6 mb-0'>{adData?.ru_name || '-'}</p>
                  </div>
                  <div className='col-md-6'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Name (Uzbek)')}</label>
                    <p className='fw-bold fs-6 mb-0'>{adData?.uz_name || '-'}</p>
                  </div>
                  <div className='col-md-6'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Promo Code')}</label>
                    <p className='fw-bold fs-6 mb-0'>{adData?.promocode || '-'}</p>
                  </div>
                </div>

                {(adData?.en_description || adData?.ru_description || adData?.uz_description) && (
                  <div className='separator my-4' />
                )}

                {adData?.en_description && (
                  <div className='mb-4'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Description (English)')}</label>
                    <div
                      className='fw-semibold fs-6'
                      dangerouslySetInnerHTML={{ __html: adData.en_description }}
                    />
                  </div>
                )}
                {adData?.ru_description && (
                  <div className='mb-4'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Description (Russian)')}</label>
                    <div
                      className='fw-semibold fs-6'
                      dangerouslySetInnerHTML={{ __html: adData.ru_description }}
                    />
                  </div>
                )}
                {adData?.uz_description && (
                  <div className='mb-0'>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Description (Uzbek)')}</label>
                    <div
                      className='fw-semibold fs-6'
                      dangerouslySetInnerHTML={{ __html: adData.uz_description }}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Gallery Images */}
            {mediaImages.length > 0 && (
              <div className='card card-flush'>
                <div className='card-header pt-5'>
                  <h4 className='card-title fw-bold'>{t('Gallery Images')}</h4>
                  <div className='card-toolbar'>
                    <span className='badge badge-light-primary'>{mediaImages.length}</span>
                  </div>
                </div>
                <div className='card-body pt-2'>
                  <div className='row g-3'>
                    {mediaImages.map((img: any) => (
                      <div className='col-6 col-md-4' key={img.adsmediaId}>
                        <img
                          src={`${imageBaseUrl}${img.image}`}
                          alt='gallery'
                          className='rounded w-100 cursor-pointer'
                          style={{ height: '140px', objectFit: 'cover' }}
                          onClick={() => setLightboxImg(`${imageBaseUrl}${img.image}`)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div className='col-lg-4 d-flex flex-column gap-6'>

            {/* Placement */}
            <div className='card card-flush'>
              <div className='card-header pt-5'>
                <h4 className='card-title fw-bold'>{t('Placement')}</h4>
              </div>
              <div className='card-body pt-2 d-flex flex-column gap-3'>
                <div>
                  <label className='text-muted fw-semibold fs-7 mb-1'>{t('Placement')}</label>
                  <p className='fw-bold fs-6 mb-0'>{adData?.placementName || locationState?.placementName || '-'}</p>
                </div>
                <div>
                  <label className='text-muted fw-semibold fs-7 mb-1'>{t('Duration')}</label>
                  <p className='fw-bold fs-6 mb-0'>
                    {adData?.duration && adData?.durationType
                      ? `${adData.duration} ${adData.durationType}`
                      : '-'}
                  </p>
                </div>
                {adData?.startDate && (
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Start Date')}</label>
                    <p className='fw-bold fs-6 mb-0'>{moment(adData.startDate).format('DD.MM.YYYY')}</p>
                  </div>
                )}
                {adData?.endDate && (
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('End Date')}</label>
                    <p className='fw-bold fs-6 mb-0'>{moment(adData.endDate).format('DD.MM.YYYY')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Budget & Payment */}
            <div className='card card-flush'>
              <div className='card-header pt-5'>
                <h4 className='card-title fw-bold'>{t('Budget & Payment')}</h4>
              </div>
              <div className='card-body pt-2 d-flex flex-column gap-3'>
                <div>
                  <label className='text-muted fw-semibold fs-7 mb-1'>{t('Price per Day')}</label>
                  <p className='fw-bold fs-6 mb-0'>
                    {adData?.perDayPrice != null ? `${adData.perDayPrice} UZS` : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-muted fw-semibold fs-7 mb-1'>{t('Total Price')}</label>
                  <p className='fw-bold fs-6 mb-0'>
                    {adData?.price != null ? `${adData.price} UZS` : '-'}
                  </p>
                </div>
                <div>
                  <label className='text-muted fw-semibold fs-7 mb-1'>{t('Payment Status')}</label>
                  <p className='fw-bold fs-6 mb-0'>
                    {adData?.paymentStatus != null ? String(adData.paymentStatus) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Targeting */}
            {Array.isArray(adData?.targets) && adData.targets.length > 0 && (
              <div className='card card-flush'>
                <div className='card-header pt-5'>
                  <h4 className='card-title fw-bold'>{t('Targeting')}</h4>
                </div>
                <div className='card-body pt-2 d-flex flex-column gap-3'>
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Age Range')}</label>
                    <p className='fw-bold fs-6 mb-0'>{getTargetValue(adData.targets, 1)}</p>
                  </div>
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Gender')}</label>
                    <p className='fw-bold fs-6 mb-0 text-capitalize'>{getTargetValue(adData.targets, 2)}</p>
                  </div>
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('User Level')}</label>
                    <p className='fw-bold fs-6 mb-0 text-capitalize'>{getTargetValue(adData.targets, 3)}</p>
                  </div>
                  <div>
                    <label className='text-muted fw-semibold fs-7 mb-1'>{t('Region')}</label>
                    <p className='fw-bold fs-6 mb-0'>{getTargetValue(adData.targets, 4)}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Promotion Items */}
            {(promotionLoading || promotionChildren.length > 0) && (
              <div className='card card-flush'>
                <div className='card-header pt-5'>
                  <h4 className='card-title fw-bold'>{t('Promotion Items')}</h4>
                  {!promotionLoading && (
                    <div className='card-toolbar'>
                      <span className='badge badge-light-primary'>{promotionChildren.length}</span>
                    </div>
                  )}
                </div>
                <div className='card-body pt-2'>
                  {promotionLoading ? (
                    <div className='text-muted fs-7'>{t('Loading')}…</div>
                  ) : (
                    <>
                      {promotionParentName && (
                        <div className='mb-3'>
                          <label className='text-muted fw-semibold fs-7 mb-1'>
                            {t(CATEGORY_CONFIG[sessionStorage.getItem('categoryId') || '']?.parentLabel || 'Parent')}
                          </label>
                          <p className='fw-bold fs-6 mb-0'>{promotionParentName}</p>
                        </div>
                      )}
                      <label className='text-muted fw-semibold fs-7 mb-2 d-block'>
                        {t(CATEGORY_CONFIG[sessionStorage.getItem('categoryId') || '']?.childLabel || 'Items')}
                      </label>
                      <div className='d-flex flex-wrap gap-2'>
                        {promotionChildren.map((child) => (
                          <span key={child.id} className='badge badge-light-primary fs-7 px-3 py-2'>
                            {child.name}
                          </span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  )
}

export default AdvertisementDetail
